import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
} from "firebase/firestore";
import { pdf } from "@react-pdf/renderer";
import SolicitudPDF from "./SolicitudPDF";
import { db } from "./Firebase";
import styled from "styled-components";
import { BsFiletypePdf } from "react-icons/bs";
import { GoChecklist } from "react-icons/go";
import MoonLoader from "react-spinners/MoonLoader";
import ReactPaginate from "react-paginate";
import Modal from "react-modal";
import { userName } from "./UserData";

Modal.setAppElement("#root");

// Estilo de la tabla y otros componentes
const ContenedorDiv = styled.div`
  margin: 1rem 0rem 4.5rem 0rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%; /* Ajustar al 100% del contenedor */
`;

const ContenedorTabla = styled.div`
  width: 95%; /* Contenedor que se adapta al ancho completo */
  overflow-x: auto; /* Habilita el desplazamiento horizontal para pantallas pequeñas */
`;

const Tabla = styled.table`
  width: 100%; /* La tabla ocupa todo el ancho disponible */
  min-width: 900px; /* Establece un ancho mínimo para que se desplace en pantallas pequeñas */
  border-collapse: collapse;
  margin-top: 5px;
  color: black;
  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: center;
    white-space: nowrap; /* Evitar que se dividan en múltiples líneas */
    overflow: hidden;
    text-overflow: ellipsis; /* Texto truncado con ... si sobrepasa */
    max-height: 2.5em; /* Altura fija para una línea */
    line-height: 1.5em;
    height: 2.5em;
  }
  th {
    background-color: #f2f2f2;
  }
  th.actions,
  td.actions {
    width: 60px;
    position: sticky; /* Hace que la columna sea fija */
    right: 0; /* La pega al lado derecho */
    background-color: #f2f2f2; /* Fondo claro para que resalte */
    z-index: 2; /* Asegura que esté encima del contenido */
  }
`;

const ContenedorSpin = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 30vh;
  gap: 10px;
`;

const InputBusqueda = styled.input`
  margin-bottom: 5px;
  padding: 5px;
  width: 50%;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const BotonAccion = styled.button`
  margin-right: 5px;
  cursor: pointer;
  padding: 5px;
  border: none;
  border-radius: 3px;
  background-color: transparent;
  &:hover {
    background-color: gray;
    color: white;
  }
`;
// Estilos de paginación
const PaginacionEstilos = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin: 16px 0px 16px;
  ul {
    display: flex;
    align-items: center;
    list-style-type: none;
    padding: 0;
  }

  li {
    text-align: center;
    min-width: 30px;
    cursor: pointer;
    border-radius: 4px;
    background-color: #f0f0f0;
    text-decoration: none;
    margin: 2px;
    color: black;
  }

  li:hover {
    background-color: #6c757d;
    color: white;
  }

  li.active {
    background-color: #c20000;
    color: white;
    font-weight: bold;
  }

  li.disabled {
    color: #ccc;
    cursor: not-allowed;
  }

  li a {
    text-decoration: none; // Eliminar el subrayado
    color: inherit; // Usar el color del elemento padre (li)
  }
`;

// Botones de paginación
const BotonPaginacion = styled.button`
  padding: 5px 10px;
  border: none;
  background-color: #c20000; /* Rojo */
  color: white;
  cursor: pointer;
  &:hover {
    background-color: #6c757d; /* Gris en hover */
  }
`;

// Estilo del modal y los botones dentro del modal
const EstiloModal = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    color: "black",
  },
};

const BotonModal = styled.button`
  padding: 8px 16px;
  border: none;
  background-color: #c20000; /* Rojo */
  color: white;
  border-radius: 4px;
  cursor: pointer;
  margin: 5px;
  &:hover {
    background-color: #6c757d; /* Gris en hover */
  }
`;

const ContenedorBotonesModal = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px; /* Separación entre botones */
`;

function SolicitudLista() {
  const [clientes, setClientes] = useState([]);
  const [paginaActual, setPaginaActual] = useState(0);
  const [solicitudesPorPagina] = useState(5);
  const [busqueda, setBusqueda] = useState("");
  const [mostrarModal, setMostrarModal] = useState(false);
  const [accionModal, setAccionModal] = useState(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [listado, setListado] = useState(false);

  // Suscribirse a los cambios en tiempo real en Firestore
  useEffect(() => {
    const clientesCollection = collection(db, "solicitudes");
    const clientesQuery = query(clientesCollection, orderBy("fecha", "desc"));

    const unsubscribe = onSnapshot(clientesQuery, (snapshot) => {
      const clientesList = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setClientes(clientesList);
      setListado(true); // Finaliza la carga
    });

    return () => unsubscribe(); // Limpia la suscripción al desmontar
  }, []);

  // Actualiza la página automáticamente según la búsqueda
  useEffect(() => {
    if (busqueda.trim() !== "") {
      const busquedaLower = busqueda.toLowerCase();

      const solicitudesFiltradas = clientes.filter((cliente) =>
        Object.values(cliente).some((valor) =>
          valor?.toString().toLowerCase().includes(busquedaLower)
        )
      );

      if (solicitudesFiltradas.length > 0) {
        const indexPrimeraCoincidencia = clientes.findIndex((cliente) =>
          Object.values(cliente).some((valor) =>
            valor?.toString().toLowerCase().includes(busquedaLower)
          )
        );

        const nuevaPagina = Math.floor(
          indexPrimeraCoincidencia / solicitudesPorPagina
        );
        setPaginaActual(
          Math.min(
            nuevaPagina,
            Math.ceil(solicitudesFiltradas.length / solicitudesPorPagina) - 1
          )
        );
      }
    }
  }, [busqueda, clientes, solicitudesPorPagina]);

  // Funciones para manejar las acciones de los modales
  const abrirModal = (accion, cliente) => {
    setAccionModal(accion);
    setClienteSeleccionado(cliente);
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
  };

  const ejecutarAccion = async () => {
    if (accionModal && clienteSeleccionado) {
      if (accionModal === "marcar") {
        await setDoc(
          doc(db, "solicitudes", clienteSeleccionado.id),
          { leida: true, marcadoPor: userName || "Usuario desconocido" },
          { merge: true }
        );
      } else if (accionModal === "descargar") {
        const docPdf = pdf(<SolicitudPDF cliente={clienteSeleccionado} />);
        const blob = await docPdf.toBlob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
      cerrarModal();
    }
  };

  // Filtra las solicitudes según la búsqueda
  const solicitudesActuales = clientes
    .filter((cliente) =>
      Object.values(cliente).some((valor) =>
        valor?.toString().toLowerCase().includes(busqueda.toLowerCase())
      )
    )
    .slice(
      paginaActual * solicitudesPorPagina,
      (paginaActual + 1) * solicitudesPorPagina
    );

  const totalPaginas = Math.ceil(
    clientes.filter((cliente) =>
      Object.values(cliente).some((valor) =>
        valor?.toString().toLowerCase().includes(busqueda.toLowerCase())
      )
    ).length / solicitudesPorPagina
  );

  // Función para manejar cambios de página
  const cambiarPagina = ({ selected }) => {
    const maxPagina =
      Math.ceil(
        clientes.filter((cliente) =>
          Object.values(cliente).some((valor) =>
            valor?.toString().toLowerCase().includes(busqueda.toLowerCase())
          )
        ).length / solicitudesPorPagina
      ) - 1;

    setPaginaActual(Math.min(selected, maxPagina));
  };

  return (
    <ContenedorDiv>
      <h4>Solicitudes Servicios Especiales</h4>
      <InputBusqueda
        type="text"
        placeholder="Buscar por número de solicitud o empresa"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
      />
      {listado ? (
        <>
          <ContenedorTabla>
            <Tabla>
              <thead>
                <tr>
                  <th>Ingreso</th>
                  <th>N°</th>
                  <th>Empresa</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Salida</th>
                  <th>Servicio</th>
                  <th className="actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {solicitudesActuales.map((cliente) => (
                  <tr
                    key={cliente.id}
                    style={{
                      backgroundColor: cliente.leida ? "#4bd1b9" : "white",
                    }}
                  >
                    <td>
                      {new Date(
                        new Date(cliente.fecha).toLocaleString("en-US", {
                          timeZone: "UTC",
                        })
                      ).toLocaleDateString("es-ES")}
                    </td>
                    <td>{cliente.docId}</td>
                    <td>{cliente.empresa}</td>
                    <td>{cliente.email}</td>
                    <td>{cliente.telefono}</td>
                    <td>
                      {new Date(
                        new Date(cliente.fechaSalida).toLocaleString("en-US", {
                          timeZone: "UTC",
                        })
                      ).toLocaleDateString("es-ES")}
                    </td>
                    <td>{cliente.tipoServicio}</td>
                    <td className="actions">
                      <BotonAccion
                        onClick={() => abrirModal("marcar", cliente)}
                        title="Marcar como leída"
                      >
                        <GoChecklist size={20} />
                      </BotonAccion>
                      <BotonAccion
                        onClick={() => abrirModal("descargar", cliente)}
                        title="Descargar PDF"
                      >
                        <BsFiletypePdf size={20} />
                      </BotonAccion>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          </ContenedorTabla>
          <PaginacionEstilos>
            <ReactPaginate
              previousLabel={<BotonPaginacion>Anterior</BotonPaginacion>}
              nextLabel={<BotonPaginacion>Siguiente</BotonPaginacion>}
              pageCount={totalPaginas}
              onPageChange={cambiarPagina}
              pageRangeDisplayed={1} // Mostrar 3 números de página intermedios
              marginPagesDisplayed={1} // Mostrar números al principio/final
              activeClassName={"active"} // Clase para la página actual
              containerClassName={"pagination"} // Clase de contenedor de paginación
              disabledClassName={"disabled"} // Clase para botones deshabilitados
              forcePage={paginaActual}
            />
          </PaginacionEstilos>
        </>
      ) : (
        <ContenedorSpin>
          <MoonLoader color="#c20000" size={50} />
          <p>Cargando listado...</p>
        </ContenedorSpin>
      )}

      {/* Modal de confirmación */}
      <Modal
        isOpen={mostrarModal}
        onRequestClose={cerrarModal}
        style={EstiloModal}
        contentLabel="Confirmar acción"
      >
        <h2>Confirmar</h2>
        <p>
          {accionModal === "marcar"
            ? clienteSeleccionado?.leida
              ? `Marcada como leída por: ${clienteSeleccionado.marcadoPor}`
              : "¿Estás seguro de que deseas marcar como leída?"
            : "¿Estás seguro de que deseas descargar el PDF?"}
        </p>
        <ContenedorBotonesModal>
          {/* Mostrar el botón Confirmar si la acción es "descargar" o si la solicitud no está marcada como leída */}
          {(accionModal === "descargar" || !clienteSeleccionado?.leida) && (
            <BotonModal onClick={ejecutarAccion}>Confirmar</BotonModal>
          )}
          <BotonModal onClick={cerrarModal}>Cancelar</BotonModal>
        </ContenedorBotonesModal>
      </Modal>
    </ContenedorDiv>
  );
}

export default SolicitudLista;
